<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Feedback', to: ROUTES.ADMIN.FEEDBACK.FEEDBACK_LIST }
          ]"
        />
      </div>
      <!-- End Page Header -->

      <!-- Feedback Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block min-w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  :fetchData="fetchFeedback"
                  searchableFields="UUID or feature"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell>Feature</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Has Comment</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell></TableCell>
                  </template>
                  <template #table-row="{ items: feedbackItems }: { items: SuperUserFeedback[] }">
                    <TableRow
                      v-for="feedback in feedbackItems"
                      :key="feedback.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="
                            ROUTES.ADMIN.FEEDBACK.FEEDBACK_DETAIL.replace(':uuid', feedback.uuid)
                          "
                        >
                          {{ feedback.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell>
                        <span
                          class="inline-flex rounded-full px-2 py-1 text-xs font-medium"
                          :class="getFeatureColor(feedback.feature)"
                        >
                          {{ getFeatureDisplayName(feedback.feature) }}
                        </span>
                      </TableCell>
                      <TableCell>
                        {{ feedback.rating }}
                      </TableCell>
                      <TableCell>
                        {{ feedback.comment ? 'Yes' : 'No' }}
                      </TableCell>
                      <CustomerTableDataCell :org="feedback.user?.org" />
                      <TableCell>
                        <router-link
                          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent text-sm font-semibold
                            text-blue-600 hover:text-blue-800 disabled:pointer-events-none disabled:opacity-50
                            dark:text-blue-500 dark:hover:text-blue-400"
                          :to="
                            ROUTES.ADMIN.FEEDBACK.FEEDBACK_DETAIL.replace(':uuid', feedback.uuid)
                          "
                          type="button"
                        >
                          Details
                        </router-link>
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { FeedbackService } from '@/types/generated/services.gen'
  import { type SuperUserFeedback, FeatureEnum } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { ROUTES } from '@/router/routes'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'
  import CustomerTableDataCell from '@/components/admin/table/CustomerTableDataCell.vue'

  const fetchFeedback = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<SuperUserFeedback>> => {
    return FeedbackService.feedbackList({
      limit: options?.limit,
      offset: options?.offset
    }) as unknown as PaginatedResponse<unknown>
  }

  const getFeatureDisplayName = (feature: string) => {
    switch (feature) {
      case FeatureEnum.AI_CHAT:
        return 'AI Chat'
      case FeatureEnum.DOCUMENT_GENERATOR:
        return 'Document Generator'
      case FeatureEnum.AI_ANALYSIS:
        return 'AI Analysis'
      case FeatureEnum.AGENT_ACTION:
        return 'Agent Action'
      case FeatureEnum.CASE_AI_WORKFLOW:
        return 'Case AI Workflow'
      case FeatureEnum.NEW_CASE_PAGE:
        return 'New Case Page'
      default:
        return feature
    }
  }

  const getFeatureColor = (feature: string) => {
    switch (feature) {
      case FeatureEnum.AI_CHAT:
        return 'bg-blue-100 text-blue-800'
      case FeatureEnum.DOCUMENT_GENERATOR:
        return 'bg-green-100 text-green-800'
      case FeatureEnum.AI_ANALYSIS:
        return 'bg-purple-100 text-purple-800'
      case FeatureEnum.AGENT_ACTION:
        return 'bg-orange-100 text-orange-800'
      case FeatureEnum.CASE_AI_WORKFLOW:
        return 'bg-teal-100 text-teal-800'
      case FeatureEnum.NEW_CASE_PAGE:
        return 'bg-indigo-100 text-indigo-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }
</script>

<style scoped></style>
